import { Link, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { InfoRow, ReadOnlyValue } from '../components/InfoRows.tsx';
import { EntityQuestionnaireTabs } from './EntityQuestionnaireTabs.tsx';
import { QuestionReviewButtons } from './QuestionReviewButtons.tsx';
import { TrainingAttemptList } from './TrainingAttemptList.tsx';
import { buildEntityQuestionnaireTabs, type QuestionnaireGroup } from '../lib/entityQuestionnaireTabs.ts';
import type { ReviewPayload } from './useReviewMutation.ts';
import type {
  AdminQuestionnaire,
  AdminSubmission,
  CompanyDocument,
  Driver,
  Question,
  QuestionCategory,
  TrainingAttempt,
} from '../lib/types.ts';

// A driver-owned submission may have a null company_id; widen the base type.
type DriverSubmission = Omit<AdminSubmission, 'company_id'> & { company_id: string | null };

export function AdminDriverDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const driverQuery = useQuery({
    queryKey: ['driver', id],
    queryFn: () => adminApi.get<{ driver: Driver }>(`/api/drivers/${id}`),
    enabled: !!id,
  });
  const questionnairesQuery = useQuery({
    queryKey: ['driver', id, 'questionnaires'],
    queryFn: () =>
      adminApi.get<{ questionnaires: AdminQuestionnaire[] }>(`/api/drivers/${id}/questionnaires`),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories'],
    queryFn: () => adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['driver-submissions', id],
    queryFn: () =>
      adminApi.get<{ submissions: DriverSubmission[] }>(`/api/drivers/${id}/submissions`),
    enabled: !!id,
  });
  const attemptsQuery = useQuery({
    queryKey: ['driver', id, 'training-attempts'],
    queryFn: () =>
      adminApi.get<{ training_attempts: TrainingAttempt[] }>(
        `/api/drivers/${id}/training-attempts`,
      ),
    enabled: !!id,
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });

  const invite = useMutation({
    mutationFn: () => adminApi.post(`/api/drivers/${id}/invite`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['driver', id] }),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ submissionId, payload }: { submissionId: string; payload: ReviewPayload }) =>
      adminApi.post(`/api/drivers/${id}/submissions/${submissionId}/review`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['driver-submissions', id] }),
  });

  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const trainingAttempts = attemptsQuery.data?.training_attempts ?? [];
  const documents = documentsQuery.data?.documents ?? [];

  const groups = useMemo(
    () => buildEntityQuestionnaireTabs(questionnaires, categories, questions),
    [questionnaires, categories, questions],
  );
  const subByQ = useMemo(() => {
    const m = new Map<string, DriverSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  if (
    driverQuery.isLoading ||
    questionnairesQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    submissionsQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  if (driverQuery.error) return <ErrorMessage error={driverQuery.error} />;
  const driver = driverQuery.data?.driver;
  if (!driver) return <ErrorMessage error={new Error('Driver not found')} />;

  // One review row: status badge, question + answer, and the review buttons
  // (only when the driver actually answered — same rule as the company page).
  const renderQuestion = (question: Question) => {
    const sub = subByQ.get(question.id);
    const busy = reviewMutation.isPending && reviewMutation.variables?.submissionId === sub?.id;
    return (
      <div className="flex items-start gap-3 py-3">
        <div className="min-w-0 flex-1">
          {sub && (
            <div className="mb-1">
              <SubmissionStatusBadge status={sub.status} />
            </div>
          )}
          <div className="text-sm font-semibold text-slate-900">{question.label}</div>
          {question.help_text && <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>}
          <div className="mt-0.5 text-sm text-slate-600">
            {(() => {
              const hasFiles = (sub?.submission_files?.length ?? 0) > 0;
              if (hasFiles) {
                return (
                  <span className="flex flex-col gap-0.5">
                    {sub!.submission_files.map((file) => (
                      <button
                        key={file.id}
                        type="button"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline text-left"
                        onClick={async () => {
                          const res = await adminApi.get<{ url: string }>(
                            `/api/drivers/${id}/submissions/${sub!.id}/files/${file.id}/download-url`,
                          );
                          window.open(res.url, '_blank', 'noopener');
                        }}
                      >
                        📎 {file.file_name}
                      </button>
                    ))}
                  </span>
                );
              }
              return answerPreview(sub);
            })()}
          </div>
        </div>
        {sub && (
          <QuestionReviewButtons
            question={question}
            status={sub.status}
            documents={documents}
            pending={busy}
            pendingDecision={busy ? reviewMutation.variables?.payload.decision : undefined}
            error={(reviewMutation.error as Error | null) ?? null}
            onDecide={(payload) => reviewMutation.mutate({ submissionId: sub.id, payload })}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/drivers" className="text-sm text-blue-600 hover:underline">
        <NavIcon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> Back to drivers
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">{driver.name}</h1>
            <CompanyStatusBadge status={driver.status} />
          </div>
          <div className="mt-1 text-sm text-slate-500">{driver.email}</div>
          <div className="mt-1 text-xs text-slate-400">
            {driver.company?.company_name ?? 'Operating company driver'}
            {driver.contractor_type ? ` · ${driver.contractor_type}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {invite.isError && <ErrorMessage error={invite.error} />}
          <Btn variant="ghost" onClick={() => invite.mutate()} loading={invite.isPending}>
            {driver.invited_at ? 'Resend invite' : 'Invite'}
          </Btn>
        </div>
      </div>

      {submissionsQuery.error && (
        <ErrorMessage error={submissionsQuery.error} />
      )}

      <EntityQuestionnaireTabs
        infoLabel="Driver information"
        infoPanel={<DriverInfoPanel driver={driver} />}
        groups={groups}
        tabBadge={(g) => pendingDecisionCount(g, subByQ)}
        renderQuestion={renderQuestion}
        ariaLabel="Driver review sections"
      />

      <TrainingAttemptList attempts={trainingAttempts} />
    </div>
  );
}

// Read-only driver detail.
function DriverInfoPanel({ driver }: { driver: Driver }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 md:max-w-xl">
      <h3 className="mb-1 text-base font-semibold text-slate-800">Details</h3>
      <InfoRow label="Name">
        <ReadOnlyValue value={driver.name} />
      </InfoRow>
      <InfoRow label="Email">
        <ReadOnlyValue value={driver.email} />
      </InfoRow>
      <InfoRow label="Phone">
        <ReadOnlyValue value={driver.phone} />
      </InfoRow>
      <InfoRow label="Owner">
        <ReadOnlyValue value={driver.company?.company_name ?? 'Operating company'} />
      </InfoRow>
      <InfoRow label="Contractor type">
        <ReadOnlyValue value={driver.contractor_type} />
      </InfoRow>
      <InfoRow label="Invited">
        <ReadOnlyValue value={driver.invited_at ? new Date(driver.invited_at).toLocaleString() : null} />
      </InfoRow>
      <InfoRow label="Submitted">
        <ReadOnlyValue value={driver.submitted_at ? new Date(driver.submitted_at).toLocaleString() : null} />
      </InfoRow>
    </div>
  );
}

// Minimal answer preview for a driver submission (review actions are the focus;
// the company page's richer signature/option rendering is intentionally omitted).
function answerPreview(sub: DriverSubmission | undefined): string {
  if (!sub) return 'Not answered yet';
  const a = sub.answer;
  if (a === null || a === undefined || a === '') return 'Not answered yet';
  if (Array.isArray(a)) return (a as string[]).join(', ');
  if (typeof a === 'boolean') return a ? 'Yes' : 'No';
  if (typeof a === 'object') return 'Answered';
  return String(a);
}

// Tab badge: outstanding reviewer decisions in this questionnaire (pending or
// rejected), matching the company page.
function pendingDecisionCount(
  group: QuestionnaireGroup,
  subByQ: Map<string, DriverSubmission>,
): number {
  let n = 0;
  for (const section of group.sections) {
    for (const q of section.questions) {
      const sub = subByQ.get(q.id);
      if (sub && (sub.status === 'pending' || sub.status === 'rejected')) n += 1;
    }
  }
  return n;
}
